import React, { FC, useCallback, useEffect, useState } from 'react';

import { useDialog } from 'hooks/useDialog';

import { RaaDialog, RaaExpandablePreview, RaaRadioButton, RaaSpinner } from 'raa-react';

import DokumentationsresursPresentationModal from 'components/dokumentationsresursPresentationModal/DokumentationsresursPresentationModal';
import { TextButton } from 'components/sharedElements/sharedElements';
import SkeletonImage from 'components/skeleton/SkeletonImage';
import SkeletonItem from 'components/skeleton/SkeletonItem';
import FotografiModal from 'components/utkast/fotografiModal/FotografiModal';
import SituationsplanModal from 'components/utkast/situationsplanModal/SituationsplanModal';

import {
  BildResursData,
  DokumentCommonResursData,
  GranskningResursIndex,
  RitningResursData,
  Vinjett,
  VinjettPutParam
} from 'models';

import { DokumentationstypEnum, VinjettSourceEnum } from 'enums';

import { RadioGroup } from '@mui/material';

import { useLazyGetArkivresursQuery } from 'api/endpoints/arkivresursSlice';
import classNames from 'classnames';
import { formatDate } from 'utilities/dateUtilities';
import { ImageDimensions, getImageDimensions } from 'utilities/imageUtilities';
import { isBildResursData, isGranskningBildMetadata, isGranskningRitningMetadata } from 'utilities/typeGuards';

interface FotografiListProps {
  dokumentation: Array<GranskningResursIndex>;
  resurser: Array<BildResursData | RitningResursData>; // Resurser från Arkivet samt Mellanlagret
  utkastId: string;
  vinjett: Vinjett | null;
  deleteResurs: (resursId: string) => void;
  onSelectVinjett?: (utkastId: string, vinjettParam: VinjettPutParam) => void;
}

const FotografiList: FC<FotografiListProps> = ({
  dokumentation,
  resurser,
  utkastId,
  vinjett,
  deleteResurs,
  onSelectVinjett
}) => {
  const [loadAll, setLoadAll] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>('');

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    const vinjettPutParam = value.startsWith('http')
      ? { typ: VinjettSourceEnum.ARKIVERAD_RESURS, arkivreferens_uri: value }
      : { typ: VinjettSourceEnum.MELLANLAGRAD_RESURS, resurs_id: value };

    if (onSelectVinjett) {
      onSelectVinjett(utkastId, vinjettPutParam);
    }
  };

  useEffect(() => {
    if (vinjett === null || vinjett?.typ === VinjettSourceEnum.MELLANLAGRAD_RESURS) {
      setSelectedValue(dokumentation.find((d) => d.vinjett)?.resurs_id || '');
    } else if (vinjett?.typ === VinjettSourceEnum.ARKIVERAD_RESURS) {
      setSelectedValue(vinjett.url);
    }
  }, [vinjett, dokumentation]);

  return (
    <>
      {resurser.length > 0 && (
        <RaaExpandablePreview height={130} ariaLabel="Lista med fotografier" onShow={() => setLoadAll(true)}>
          <RadioGroup value={selectedValue} onChange={handleChange}>
            <ul className="tw-flex tw-flex-col tw-gap-y-4">
              <li className="tw--mb-4 tw-flex tw-flex-row tw-gap-x-4">
                <div className="tw-basis-3/12" />
                <div className="tw-basis-5/12" />
                <div className="tw-basis-2/12 tw-text-center">Vinjettbild</div>
                <div className="tw-basis-2/12 tw-text-center">Radera</div>
              </li>
              {resurser.map((item, index) => (
                <li key={`foto-${index}`} className="tw-flex tw-gap-x-4">
                  {(item.uri && <Arkivresurs item={item} itemIndex={index} shouldLoad={loadAll} />) || (
                    <Mellanlagerresurs
                      item={item}
                      itemIndex={index}
                      dokumentation={dokumentation}
                      deleteResurs={deleteResurs}
                    />
                  )}
                </li>
              ))}
            </ul>
          </RadioGroup>
        </RaaExpandablePreview>
      )}
    </>
  );
};

const Arkivresurs: FC<{
  item: BildResursData | RitningResursData;
  itemIndex: number;
  shouldLoad: boolean;
}> = ({ item, itemIndex, shouldLoad }) => {
  const { setDialog, unsetDialog } = useDialog();
  const [getArkivresurs] = useLazyGetArkivresursQuery();

  const openFotoModal = useCallback(
    async (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.preventDefault();

      if (item && item.uri) {
        const { data: resurs, isSuccess } = await getArkivresurs(item.uri);

        if (isSuccess) {
          setDialog(DokumentationsresursPresentationModal, { resurs, onClose: () => unsetDialog() });
        }
      }
    },
    [getArkivresurs, item, setDialog, unsetDialog]
  );

  return (
    <>
      <div className="tw-basis-3/12">
        <button className="tw-w-full" onClick={(e) => openFotoModal(e)}>
          <SkeletonImage
            className="tw-h-20 tw-w-full tw-object-cover"
            skeletonClassNames="tw-h-20 tw-w-full"
            src={item.thumbnailUrl}
            animated={shouldLoad}
            alt="Uppladdad bild"
          />
        </button>
      </div>
      <div className="tw-basis-5/12">
        {(item.titel === undefined && (
          <>
            <SkeletonItem classNames="tw-h-6 tw-w-32" rounded="md" animated={shouldLoad} />
            <SkeletonItem classNames="tw-h-6 tw-w-24 tw-mt-1" rounded="md" animated={shouldLoad} />
            <SkeletonItem classNames="tw-h-6 tw-w-20 tw-mt-1" rounded="md" animated={shouldLoad} />
          </>
        )) || (
          <>
            <TextButton onClick={(e) => openFotoModal(e)}>{item?.titel}</TextButton>
            {item && isBildResursData(item) && (
              <span
                className={classNames('tw-block', {
                  'tw-hidden': !item?.upphovsman
                })}
              >{`${item?.upphovsman}`}</span>
            )}
            <span className="tw-block">{formatDate(item?.tillkomstdatum)}</span>
          </>
        )}
      </div>
      <div className="tw-flex tw-basis-2/12 tw-place-items-center tw-justify-center">
        <span className="tw-inline-block tw-w-5">
          <RaaRadioButton
            id={`utkast-vinjett-${itemIndex}`}
            name="utkast-vinjett"
            value={item.uri}
            labelText="Välj bild som vinjett"
            labelSrOnly={true}
          />
        </span>
      </div>
      <div className="tw-flex tw-basis-2/12 tw-place-items-center tw-justify-center tw-text-size-1.313" />
    </>
  );
};

interface MellanlagerresursProps {
  item: DokumentCommonResursData;
  itemIndex: number;
  dokumentation: Array<GranskningResursIndex>;
  deleteResurs: (resursId: string) => void;
}

const Mellanlagerresurs: FC<MellanlagerresursProps> = ({ item, itemIndex, dokumentation, deleteResurs }) => {
  const { setDialog, unsetDialog } = useDialog();
  const [imgCacheKey, setImgCacheKey] = useState<string>('default');

  const confirmDeleteResurs = useCallback(
    (resursId: string, titel: string) => {
      setDialog(RaaDialog, {
        header: 'Bekräfta radering',
        children: (
          <p id="dialog-description" className="tw-my-4">
            Är du säker på att du vill radera {titel}?
          </p>
        ),
        cancel: () => unsetDialog(),
        cancelButtonText: 'Avbryt',
        proceedButtonText: 'Radera',
        proceed: () => {
          deleteResurs(resursId);
          unsetDialog();
        },
        ariaDescribedBy: 'dialog-description'
      });
    },
    [deleteResurs, setDialog, unsetDialog]
  );

  const editFotografi = useCallback(
    async (item: GranskningResursIndex, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const toCommonDialogProps = (item: GranskningResursIndex, file: File, dimensions?: ImageDimensions) => ({
        resursId: item.resurs_id,
        titel: item.resurs_metadata.titel,
        file: file,
        licens: item.resurs_metadata.licens_id,
        utkastId: item.utkast_id!,
        height: dimensions?.height ?? 0,
        width: dimensions?.width ?? 0,
        date: new Date(item.resurs_metadata.tillkomstdatum),
        onDelete: (resursId: string, titel: string) => {
          unsetDialog();
          confirmDeleteResurs(resursId, titel);
        },
        onSubmit: () => {
          setImgCacheKey(Math.random().toString(36));
          unsetDialog();
        }
      });

      event?.preventDefault();
      setDialog(RaaDialog, {
        header: 'Laddar bild',
        ariaDescribedBy: 'dialog-description',
        children: (
          <div className="tw-flex tw-justify-center tw-py-12">
            <RaaSpinner size="3rem" />
            <span id="dialog-description" className="tw-ml-2 tw-self-center">
              Vänta...
            </span>
          </div>
        )
      });

      const response = await fetch(
        `${process.env.PUBLIC_URL}/api/bebyggelseobjekt/utkast/${item.utkast_id}/dokumentation/${item.resurs_id}/original.${item.file_extension}`
      );
      const blob = await response.blob();
      const file = new File([blob], item.filename, { type: item.media });
      const dimensions = await getImageDimensions(file);

      unsetDialog();

      {
        item.dokumentationstyp === DokumentationstypEnum.BILD &&
          isGranskningBildMetadata(item.resurs_metadata) &&
          setDialog(FotografiModal, {
            ...toCommonDialogProps(item, file, dimensions),
            bildtext: item.resurs_metadata.bildtext,
            upphovsmanFornamn: item.resurs_metadata.upphovsman_fornamn,
            upphovsmanEfternamn: item.resurs_metadata.upphovsman_efternamn,
            vy: item.resurs_metadata.vy
          });
      }

      {
        item.dokumentationstyp === DokumentationstypEnum.RITNING &&
          isGranskningRitningMetadata(item.resurs_metadata) &&
          setDialog(SituationsplanModal, {
            ...toCommonDialogProps(item, file, dimensions),
            motivbeskrivning: item.resurs_metadata.motivbeskrivning,
            vy: item.resurs_metadata.vy
          });
      }
    },
    [confirmDeleteResurs, setDialog, unsetDialog]
  );

  return (
    <>
      <div className="tw-basis-3/12">
        <button
          className="tw-box-border tw-w-full tw-border-4 tw-border-b-0 tw-border-raa-green"
          onClick={(e) => editFotografi(dokumentation.find((d) => d.resurs_id === item.resursId)!, e)}
        >
          <SkeletonImage
            className="tw-h-20 tw-w-full tw-object-cover"
            src={`${process.env.PUBLIC_URL}/api${item.thumbnailUrl}?cacheKey=${imgCacheKey}`}
            alt="Vald bild"
          />
          <span className="tw-block tw-bg-raa-green tw-py-1 tw-text-center tw-text-size-0.813 tw-text-raa-white">
            <span className="raa-icon-edit tw-mr-1"></span>
            Utkast
          </span>
        </button>
      </div>
      <div className="tw-basis-5/12">
        <TextButton onClick={(e) => editFotografi(dokumentation.find((d) => d.resurs_id === item.resursId)!, e)}>
          {item.titel}
        </TextButton>
        {'upphovsman' in item && (
          <span
            className={classNames('tw-block', {
              'tw-hidden': !item.upphovsman && item.upphovsman
            })}
          >
            {`${item.upphovsman}`}
          </span>
        )}
        <span className="tw-block">{formatDate(item.tillkomstdatum)}</span>
      </div>
      <div className="tw-flex tw-basis-2/12 tw-place-items-center tw-justify-center">
        <span className="tw-inline-block tw-w-5">
          <RaaRadioButton
            id={`utkast-vinjett-${itemIndex}`}
            name="utkast-vinjett"
            value={item.resursId}
            labelText="Välj bild som vinjett"
            labelSrOnly={true}
          />
        </span>
      </div>
      <div className="tw-flex tw-basis-2/12 tw-place-items-center tw-justify-center tw-text-size-1.313">
        <TextButton onClick={() => confirmDeleteResurs(item.resursId!, item.titel!)} icon="raa-icon-delete">
          <span className="tw-sr-only">Radera</span>
        </TextButton>
      </div>
    </>
  );
};

export default FotografiList;
